<i18n src="./translations"></i18n>

<template>
  <ExpandableTableSection
    :trigger="trigger"
    :table="table"
    :preview="{ limit: carTypesPreviewCount }"
    :expanded="expanded"
    @tableLoaded="onTableLoaded"
    @tableIsEmpty="$emit('empty', 'carTypes')"
  >
    <template #model="{ entity }">
      <div
        v-if="isMatch(entity)"
        class="match-info"
        data-testid="matching-car-info"
      >
        <Icon class="icon" name="check" width="16" height="16" />
        <span class="label">{{ $t('pdp-tab-car-types.car-match-label') }}</span>
      </div>

      <p>
        <b class="brand">{{ carModel(entity.model).model }}</b>
        <span class="type">{{ carModel(entity.model).specs }}</span>
      </p>
    </template>
  </ExpandableTableSection>
</template>

<script>
import ExpandableTableSection from 'Components/02-molecules/expandable-table-section/ExpandableTableSection';
import globalMixin from 'Libs/mixins/globalMixin';
import Icon from 'Src/components/00-generated/Icon.vue';
import { mapState } from 'vuex';

export default {
  name: 'PdpTabCarTypes',
  components: { ExpandableTableSection, Icon },
  mixins: [globalMixin],
  props: {
    productId: { type: String, required: true },
  },
  data() {
    return {
      carTypesCount: null,
      carTypesPreviewCount: 4,
    };
  },
  computed: {
    ...mapState('core', {
      ktypnr: ({ placeholders }) => {
        const hasVehicle =
          placeholders?.carSelection?.data?.vehicle !== undefined;
        const vehicle = hasVehicle && placeholders.carSelection.data.vehicle;
        return vehicle ? vehicle.ktypnr : null;
      },
    }),
    ajaxUrl() {
      if (!this.productId) return '';
      return this.interpolateUrl(
        this.frontFacingPath('search.ajax.product.car.data'),
        { productId: this.productId }
      );
    },
    trigger() {
      return {
        show: this.carTypesCount > this.carTypesPreviewCount,
        title: this.$t('pdp-tab-car-types.show-all-car-types-count', {
          '%count%': this.carTypesCount,
        }),
        modifiers: ['secondary', 'slim'],
      };
    },
    expanded() {
      return {
        title: this.$t('pdp-tab-car-types.suitable-cars'),
        limit: 10,
      };
    },
    table() {
      return {
        isAjax: true,
        url: this.ajaxUrl,
        loadMoreLabel: this.$t('pdp-tab-car-types.more-car-types'),
        tableKey: 'carTypes',
        hasBorder: false,
        showHeaders: false,
        transform: this.sortCarTypes,
        rowClass: this.rowClass,
      };
    },
  },
  methods: {
    carModel(value = '') {
      let level = 0;

      for (let i = 0; i < value.length; i++) {
        const c = value[i];

        if (c === '(') level++;
        if (c === ')') level--;

        if (
          level === 0 &&
          c === ',' &&
          value[i + 1] === ' ' &&
          /\d/.test(value[i + 2])
        ) {
          return {
            model: value.slice(0, i).trim(),
            specs: value.slice(i + 2).trim(),
          };
        }
      }

      return {
        model: value.trim(),
        specs: '',
      };
    },
    onTableLoaded(payload) {
      if (payload?.tableKey !== 'carTypes') return;
      if (!Number.isFinite(payload?.count)) return;
      this.carTypesCount = payload.count;
    },
    interpolateUrl(path, params) {
      const interpolate = this.$options.filters?.interpolate;
      if (typeof interpolate === 'function') return interpolate(path, params);
      return path?.replace('{productId}', this.productId);
    },
    isMatch(entity) {
      if (!entity || entity.carId === undefined || entity.carId === null) {
        return false;
      }

      if (this.ktypnr === undefined || this.ktypnr === null) {
        return false;
      }

      return entity.carId.toString() === this.ktypnr.toString();
    },
    sortCarTypes(entities) {
      if (!Array.isArray(entities) || !this.ktypnr) return entities;

      return [...entities].sort(
        (a, b) => (this.isMatch(b) ? 1 : 0) - (this.isMatch(a) ? 1 : 0)
      );
    },
    rowClass(entity) {
      return this.isMatch(entity) ? 'match-row' : '';
    },
  },
};
</script>

<style scoped lang="scss">
.match-info {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--font-size-SM);
  font-weight: bold;
  white-space: nowrap;

  .icon {
    color: var(--color-kfzteile24-semantic-green);
  }
}

.brand {
  margin-right: var(--space-0--half);
  font-weight: bold;
}

.type {
  color: var(--color-charcoal-gray);
}
</style>
